/**
 * Single source of truth for role-based routing.
 *
 * Used by:
 *   - /app/frontend/src/components/RouteGuard.jsx  (declarative redirect of
 *     the entire <Routes> tree based on the current user's role)
 *   - /app/frontend/src/pages/Login.jsx            (post-login redirect)
 *   - /app/frontend/src/App.js                     (root `/` fallback)
 *
 * Keeping this in one file prevents the kind of drift that caused the
 * /dashboard infinite redirect loop and the stale-role admin/staff bounce
 * issues in earlier iterations.
 */

export function isStaffUser(user) {
  if (!user) return false;
  return user.role === 'staff' || user.industryType === 'staff' || user.isStaff === true;
}

export function isVendorUser(user) {
  if (!user) return false;
  return user.role === 'vendor' || user.industryType === 'vendor' || user.isVendor === true;
}

export function isCompanyUser(user) {
  // Admin, manager, owner — anyone allowed in the admin chrome.
  if (!user) return false;
  return !isStaffUser(user) && !isVendorUser(user);
}

export function getHomeRoute(user) {
  if (!user) return '/login';
  if (isStaffUser(user)) return '/staff-portal';
  // Vendor self-service was retired; any lingering vendor-role user
  // falls back to the tenant home.
  return '/control-center';
}

/* Route allow-lists per role group.
 *
 * `RegExp` patterns are matched against `location.pathname`. Routes that
 * don't appear in the user's allow-list trigger a redirect to that user's
 * home route. Public/standalone routes (handled by App.js's outer `if`)
 * bypass the guard entirely.
 */

const STAFF_ALLOWED = [
  /^\/staff-portal($|\/)/,
  /^\/staff\//,
  /^\/my-events($|\/)/,
  /^\/my-availability($|\/)/,
  /^\/my-chats($|\/)/,
  /^\/notifications($|\/)/,
  /^\/profile($|\/)/,
  /^\/team-chat($|\/)/,
  /^\/announcements($|\/)/,
  /^\/leaderboard($|\/)/,
  /^\/messages($|\/)/,
];

export function isPathAllowed(user, pathname) {
  if (!user) return false;
  if (isStaffUser(user)) return STAFF_ALLOWED.some((re) => re.test(pathname));
  // Vendor self-service is retired. Any lingering vendor-role user has
  // no allow-list — RouteGuard bounces them to getHomeRoute (tenant home).
  if (isVendorUser(user)) return false;
  // Admin/manager/owner — everything is allowed by default.
  return true;
}
